/**
 * Sync Settings Modal
 * 
 * Pre-sync confirmation and configuration modal for API integrations
 * Shows estimated item count, last sync stats, and API-specific filters
 */

import React, { useState, useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
  Modal,
  Button,
  Input,
  Select,
  Badge,
  LoadingSpinner,
} from '@tracekit/core';
import { previewSyncAPIIntegration } from '@tracekit/core/api/sources';
import { Clock, Database, Filter, Info, AlertTriangle } from 'lucide-react';

export interface SyncSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (filters: Record<string, any>) => void;
  sourceId: string;
  sourceName: string;
  integrationType: string;
  initialFilters?: Record<string, any>;
}

export const SyncSettingsModal: React.FC<SyncSettingsModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  sourceId,
  sourceName,
  integrationType,
  initialFilters = {},
}) => {
  const [filters, setFilters] = useState<Record<string, any>>(initialFilters);
  const [saveAsDefault, setSaveAsDefault] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);

  // Preview mutation
  const previewMutation = useMutation({
    mutationFn: async () => {
      if (!sourceId || sourceId.trim() === '') {
        throw new Error('Source ID is required for preview');
      }
      return await previewSyncAPIIntegration(sourceId, filters);
    },
    onSuccess: (data) => {
      setPreviewData(data);
    },
    onError: (error: any) => {
      console.error('[SyncSettingsModal] Preview failed:', error);
    },
  });

  // Load preview when modal opens or filters change
  useEffect(() => {
    if (isOpen && sourceId && sourceId.trim() !== '') {
      previewMutation.mutate();
    } else if (isOpen && (!sourceId || sourceId.trim() === '')) {
      console.warn('[SyncSettingsModal] Cannot load preview: sourceId is empty');
    }
  }, [isOpen, sourceId]);

  // Refresh preview when filters change (debounced)
  useEffect(() => {
    if (!isOpen || !sourceId || sourceId.trim() === '') return;
    
    const timeoutId = setTimeout(() => {
      previewMutation.mutate();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [filters, isOpen, sourceId]);

  const handleConfirm = () => {
    // Validate filters before confirming
    const validationErrors = validateFilters(integrationType, filters);
    
    if (validationErrors.length > 0) {
      // In a real implementation, show these errors in the UI
      console.error('[SYNC SETTINGS] Validation errors:', validationErrors);
      alert(`Validation errors:\n${validationErrors.join('\n')}`);
      return;
    }
    
    onConfirm({
      ...filters,
      saveAsDefault,
    });
  };
  
  // Validate filter combinations
  const validateFilters = (integrationType: string, filters: Record<string, any>): string[] => {
    const errors: string[] = [];
    
    switch (integrationType) {
      case 'samgov':
        // Validate date ranges
        if (filters.postedFrom && filters.postedTo) {
          const fromDate = new Date(filters.postedFrom);
          const toDate = new Date(filters.postedTo);
          if (fromDate > toDate) {
            errors.push('Posted From date must be before Posted To date');
          }
        }
        
        // Validate limit
        if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
          errors.push('Limit must be between 1 and 1000');
        }
        break;
      
      case 'canada_grants':
        if (filters.limit && (filters.limit < 1 || filters.limit > 500)) {
          errors.push('Limit must be between 1 and 500');
        }
        break;
      
      case 'worldbank':
        if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
          errors.push('Limit must be between 1 and 1000');
        }
        break;
      
      case 'usa_awards':
        // Validate amount range
        if (filters.minAmount && filters.maxAmount) {
          if (parseFloat(filters.minAmount) > parseFloat(filters.maxAmount)) {
            errors.push('Minimum amount must be less than maximum amount');
          }
        }
        
        // Validate date range
        if (filters.fromDate && filters.toDate) {
          const fromDate = new Date(filters.fromDate);
          const toDate = new Date(filters.toDate);
          if (fromDate > toDate) {
            errors.push('From date must be before To date');
          }
        }
        
        if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
          errors.push('Limit must be between 1 and 1000');
        }
        break;
      
      case 'federal_register':
        if (filters.limit && (filters.limit < 1 || filters.limit > 1000)) {
          errors.push('Limit must be between 1 and 1000');
        }
        break;
      
      case 'rss_feeds':
        if (filters.max_items_per_feed && (filters.max_items_per_feed < 1 || filters.max_items_per_feed > 500)) {
          errors.push('Max items per feed must be between 1 and 500');
        }
        break;
    }
    
    return errors;
  };

  const renderFilterSection = () => {
    switch (integrationType) {
      case 'samgov':
        return <SAMGovFilters filters={filters} onChange={setFilters} />;
      
      case 'canada_grants':
        return <CanadaGrantsFilters filters={filters} onChange={setFilters} />;
      
      case 'worldbank':
        return <WorldBankFilters filters={filters} onChange={setFilters} />;
      
      case 'usa_awards':
        return <USASpendingFilters filters={filters} onChange={setFilters} />;
      
      case 'federal_register':
        return <FederalRegisterFilters filters={filters} onChange={setFilters} />;
      
      case 'rss_feeds':
        return <RSSFeedsFilters filters={filters} onChange={setFilters} />;
      
      default:
        return <GenericFilters filters={filters} onChange={setFilters} />;
    }
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    if (seconds < 60) return `${Math.round(seconds)}s`;
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
    return `${Math.round(seconds / 3600)}h ${Math.round((seconds % 3600) / 60)}m`;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Sync Settings - ${sourceName}`}
      size="large"
    >
      <div className="space-y-6">
        {/* Preview Stats */}
        {previewMutation.isPending ? (
          <div className="flex items-center justify-center p-8">
            <LoadingSpinner />
            <span className="ml-2">Loading preview...</span>
          </div>
        ) : previewData ? (
          <div className="bg-gray-50 rounded-lg p-4 space-y-4">
            <h3 className="text-lg font-semibold flex items-center">
              <Info className="w-5 h-5 mr-2" />
              Sync Preview
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              {/* Estimated Count */}
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <Database className="w-4 h-4 mr-1" />
                  Estimated Items
                </div>
                <div className="text-2xl font-bold text-blue-600">
                  {previewData.estimated_count.toLocaleString()}
                </div>
                {previewData.estimated_count === 0 && (
                  <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-sm flex items-center">
                    <AlertTriangle className="w-4 h-4 mr-2 flex-shrink-0" />
                    No items found with current filters
                  </div>
                )}
                {previewData.estimated_count > 1000 && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm flex items-center">
                    <Info className="w-4 h-4 mr-2 flex-shrink-0" />
                    Large sync - may take several minutes
                  </div>
                )}
              </div>

              {/* Last Sync Stats */}
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div className="flex items-center text-sm text-gray-500 mb-1">
                  <Clock className="w-4 h-4 mr-1" />
                  Last Sync
                </div>
                {previewData.last_sync ? (
                  <div className="space-y-1">
                    <div className="text-lg font-semibold">
                      {previewData.last_sync.item_count?.toLocaleString() || 'N/A'} items
                    </div>
                    <div className="text-sm text-gray-600">
                      {formatDuration(previewData.last_sync.duration_seconds)}
                    </div>
                    <div className="text-xs text-gray-500">
                      {previewData.last_sync.timestamp
                        ? new Date(previewData.last_sync.timestamp).toLocaleString()
                        : 'Never'}
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">Never synced</div>
                )}
              </div>
            </div>
          </div>
        ) : null}

        {/* Filters Section */}
        <div className="space-y-4">
          <h3 className="text-lg font-semibold flex items-center">
            <Filter className="w-5 h-5 mr-2" />
            Filter Settings
          </h3>
          
          {renderFilterSection()}
        </div>

        {/* Active Filters Summary */}
        {Object.keys(filters).length > 0 && (
          <div className="bg-blue-50 p-3 rounded-lg">
            <div className="text-sm font-medium text-blue-900 mb-2">Active Filters:</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(filters).map(([key, value]) => (
                <Badge key={key} variant="primary">
                  {key}: {Array.isArray(value) ? value.join(', ') : String(value)}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Save as Default */}
        <div className="flex items-center space-x-2 pt-4 border-t border-gray-200">
          <input
            type="checkbox"
            id="save-default"
            checked={saveAsDefault}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSaveAsDefault(e.target.checked)}
            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
          />
          <label htmlFor="save-default" className="text-sm text-gray-700 cursor-pointer">
            Save these settings as default for future syncs
          </label>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={previewMutation.isPending || (previewData?.estimated_count === 0)}
          >
            Start Sync
            {previewData && previewData.estimated_count > 0 && (
              <span className="ml-2">({previewData.estimated_count.toLocaleString()} items)</span>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ===== HELPER COMPONENTS =====

// Simple multi-select component
const MultiSelect: React.FC<{
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: Array<{ value: string; label: string }>;
}> = ({ label, value, onChange, options }) => {
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    onChange(selectedOptions);
  };

  return (
    <div className="w-full">
      <label className="block text-sm font-semibold text-slate-700 dark:text-gray-300 mb-2">
        {label}
      </label>
      <select
        multiple
        value={value}
        onChange={handleChange}
        className="w-full pl-4 pr-4 py-2.5 text-sm font-medium bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-slate-400 dark:hover:border-slate-500 transition-all"
        style={{ minHeight: '120px' }}
      >
        {options.map(option => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <p className="mt-1 text-xs text-gray-500">Hold Ctrl/Cmd to select multiple</p>
    </div>
  );
};

// ===== API-SPECIFIC FILTER COMPONENTS =====

const SAMGovFilters: React.FC<{
  filters: Record<string, any>;
  onChange: (filters: Record<string, any>) => void;
}> = ({ filters, onChange }) => {
  return (
    <div className="space-y-4">
      <Input
        label="Search Keywords"
        placeholder="e.g., IT services, construction"
        value={filters.q || ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, q: e.target.value })}
      />

      <MultiSelect
        label="NAICS Code"
        value={filters.naics || []}
        onChange={(value: string[]) => onChange({ ...filters, naics: value })}
        options={[
          { value: '541511', label: '541511 - Custom Computer Programming' },
          { value: '541512', label: '541512 - Computer Systems Design' },
          { value: '541513', label: '541513 - Computer Facilities Management' },
          { value: '541519', label: '541519 - Other Computer Related Services' },
          { value: '236220', label: '236220 - Commercial Building Construction' },
        ]}
      />

      <MultiSelect
        label="Set-Aside Type"
        value={filters.setAside || []}
        onChange={(value: string[]) => onChange({ ...filters, setAside: value })}
        options={[
          { value: 'SBA', label: 'Small Business Set-Aside' },
          { value: 'SBP', label: 'Small Business Partnership' },
          { value: '8AN', label: '8(a) Native American Owned' },
          { value: 'HZC', label: 'HUBZone Set-Aside' },
          { value: 'SDVOSBC', label: 'Service-Disabled Veteran-Owned' },
          { value: 'WOSB', label: 'Women-Owned Small Business' },
        ]}
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Posted From"
          type="date"
          value={filters.postedFrom || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, postedFrom: e.target.value })}
        />

        <Input
          label="Posted To"
          type="date"
          value={filters.postedTo || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, postedTo: e.target.value })}
        />
      </div>

      <Input
        label="Max Items"
        type="number"
        min="1"
        max="1000"
        value={filters.limit || 100}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, limit: parseInt(e.target.value) })}
      />
    </div>
  );
};

const CanadaGrantsFilters: React.FC<{
  filters: Record<string, any>;
  onChange: (filters: Record<string, any>) => void;
}> = ({ filters, onChange }) => {
  return (
    <div className="space-y-4">
      <MultiSelect
        label="Province/Territory"
        value={filters.provinces || []}
        onChange={(value: string[]) => onChange({ ...filters, provinces: value })}
        options={[
          { value: 'ON', label: 'Ontario' },
          { value: 'QC', label: 'Quebec' },
          { value: 'BC', label: 'British Columbia' },
          { value: 'AB', label: 'Alberta' },
          { value: 'MB', label: 'Manitoba' },
          { value: 'SK', label: 'Saskatchewan' },
          { value: 'NS', label: 'Nova Scotia' },
          { value: 'NB', label: 'New Brunswick' },
          { value: 'NL', label: 'Newfoundland and Labrador' },
          { value: 'PE', label: 'Prince Edward Island' },
          { value: 'NT', label: 'Northwest Territories' },
          { value: 'YT', label: 'Yukon' },
          { value: 'NU', label: 'Nunavut' },
        ]}
      />

      <MultiSelect
        label="Category"
        value={filters.categories || []}
        onChange={(value) => onChange({ ...filters, categories: value })}
        options={[
          { value: 'business', label: 'Business & Innovation' },
          { value: 'research', label: 'Research & Development' },
          { value: 'education', label: 'Education & Training' },
          { value: 'health', label: 'Health & Social Services' },
          { value: 'environment', label: 'Environment & Sustainability' },
          { value: 'arts', label: 'Arts & Culture' },
          { value: 'infrastructure', label: 'Infrastructure' },
        ]}
      />

      <Input
        label="Search Keywords"
        placeholder="e.g., clean technology, agriculture"
        value={filters.keywords || ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, keywords: e.target.value })}
      />

      <Input
        label="Max Items"
        type="number"
        min="1"
        max="500"
        value={filters.limit || 100}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, limit: parseInt(e.target.value) })}
      />
    </div>
  );
};

const WorldBankFilters: React.FC<{
  filters: Record<string, any>;
  onChange: (filters: Record<string, any>) => void;
}> = ({ filters, onChange }) => {
  return (
    <div className="space-y-4">
      <MultiSelect
        label="Country"
        value={filters.countrycode || []}
        onChange={(value: string[]) => onChange({ ...filters, countrycode: value })}
        options={[
          { value: 'US', label: 'United States' },
          { value: 'CA', label: 'Canada' },
          { value: 'IN', label: 'India' },
          { value: 'CN', label: 'China' },
          { value: 'BR', label: 'Brazil' },
          { value: 'ZA', label: 'South Africa' },
          { value: 'KE', label: 'Kenya' },
          { value: 'NG', label: 'Nigeria' },
        ]}
      />

      <MultiSelect
        label="Sector"
        value={filters.sectorcode || []}
        onChange={(value: string[]) => onChange({ ...filters, sectorcode: value })}
        options={[
          { value: 'AX', label: 'Education' },
          { value: 'BX', label: 'Health' },
          { value: 'CX', label: 'Water & Sanitation' },
          { value: 'DX', label: 'Energy & Mining' },
          { value: 'EX', label: 'Transportation' },
          { value: 'FX', label: 'Agriculture' },
          { value: 'GX', label: 'Environment' },
          { value: 'JX', label: 'Finance' },
        ]}
      />

      <Select
        label="Project Status"
        value={filters.status || ''}
        onChange={(value: string) => onChange({ ...filters, status: value })}
        options={[
          { value: '', label: 'All' },
          { value: 'active', label: 'Active' },
          { value: 'completed', label: 'Completed' },
          { value: 'proposed', label: 'Proposed' },
        ]}
      />

      <Input
        label="Max Items"
        type="number"
        min="1"
        max="1000"
        value={filters.limit || 100}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, limit: parseInt(e.target.value) })}
      />
    </div>
  );
};

const USASpendingFilters: React.FC<{
  filters: Record<string, any>;
  onChange: (filters: Record<string, any>) => void;
}> = ({ filters, onChange }) => {
  return (
    <div className="space-y-4">
      <Input
        label="Agency Name"
        placeholder="e.g., Department of Defense"
        value={filters.agency || ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, agency: e.target.value })}
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Min Award Amount ($)"
          type="number"
          min="0"
          value={filters.minAmount || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, minAmount: parseFloat(e.target.value) })}
        />

        <Input
          label="Max Award Amount ($)"
          type="number"
          min="0"
          value={filters.maxAmount || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, maxAmount: parseFloat(e.target.value) })}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="From Date"
          type="date"
          value={filters.fromDate || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, fromDate: e.target.value })}
        />

        <Input
          label="To Date"
          type="date"
          value={filters.toDate || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, toDate: e.target.value })}
        />
      </div>

      <Input
        label="Max Items"
        type="number"
        min="1"
        max="1000"
        value={filters.limit || 500}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, limit: parseInt(e.target.value) })}
      />
    </div>
  );
};

const FederalRegisterFilters: React.FC<{
  filters: Record<string, any>;
  onChange: (filters: Record<string, any>) => void;
}> = ({ filters, onChange }) => {
  return (
    <div className="space-y-4">
      <MultiSelect
        label="Document Type"
        value={filters.type || []}
        onChange={(value: string[]) => onChange({ ...filters, type: value })}
        options={[
          { value: 'RULE', label: 'Rule' },
          { value: 'PRORULE', label: 'Proposed Rule' },
          { value: 'NOTICE', label: 'Notice' },
          { value: 'PRESDOCU', label: 'Presidential Document' },
        ]}
      />

      <MultiSelect
        label="Agencies"
        value={filters.agencies || []}
        onChange={(value: string[]) => onChange({ ...filters, agencies: value })}
        options={[
          { value: 'environmental-protection-agency', label: 'EPA' },
          { value: 'health-and-human-services-department', label: 'HHS' },
          { value: 'defense-department', label: 'DoD' },
          { value: 'treasury-department', label: 'Treasury' },
          { value: 'justice-department', label: 'DOJ' },
        ]}
      />

      <Input
        label="Search Keywords"
        placeholder="e.g., cybersecurity, climate"
        value={filters.keywords || ''}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, keywords: e.target.value })}
      />

      <Input
        label="Max Items"
        type="number"
        min="1"
        max="1000"
        value={filters.limit || 100}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, limit: parseInt(e.target.value) })}
      />
    </div>
  );
};

const RSSFeedsFilters: React.FC<{
  filters: Record<string, any>;
  onChange: (filters: Record<string, any>) => void;
}> = ({ filters, onChange }) => {
  return (
    <div className="space-y-4">
      <Input
        label="Max Items Per Feed"
        type="number"
        min="1"
        max="500"
        value={filters.max_items_per_feed || 100}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, max_items_per_feed: parseInt(e.target.value) })}
      />

      <Select
        label="Feed Categories"
        multiple
        value={filters.categories || []}
        onChange={(value) => onChange({ ...filters, categories: value })}
        options={[
          { value: 'government', label: 'Government & Policy' },
          { value: 'procurement', label: 'Procurement News' },
          { value: 'technology', label: 'Technology' },
          { value: 'industry', label: 'Industry Updates' },
        ]}
      />

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Published After"
          type="date"
          value={filters.publishedAfter || ''}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, publishedAfter: e.target.value })}
        />
      </div>
    </div>
  );
};

const GenericFilters: React.FC<{
  filters: Record<string, any>;
  onChange: (filters: Record<string, any>) => void;
}> = ({ filters, onChange }) => {
  return (
    <div className="space-y-4">
      <Input
        label="Max Items"
        type="number"
        min="1"
        max="1000"
        value={filters.limit || 100}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange({ ...filters, limit: parseInt(e.target.value) })}
      />

      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm">
        No specific filters available for this integration type.
        Only general sync settings can be configured.
      </div>
    </div>
  );
};

export default SyncSettingsModal;
